import React, { useState, useEffect } from "react";
import axios from "axios";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, UserX, UserCheck, Shield, Mail, Phone, Calendar, Loader2, Store, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseSellers = () => {
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingSeller, setEditingSeller] = useState(null);
    const [editData, setEditData] = useState({ email: "", password: "", phone: "" });
    const [saving, setSaving] = useState(false);
    
    const token = localStorage.getItem('enterprise_token');

    useEffect(() => {
        fetchSellers();
    }, []);

    const fetchSellers = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.get(`${API}/admin/sellers`, { headers });
            setSellers(res.data);
        } catch (error) {
            console.error("Error fetching sellers:", error);
            toast.error("Pa kapab jwenn lis machann yo.");
        } finally {
            setLoading(false);
        }
    };

    const toggleSellerStatus = async (sellerId) => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.post(`${API}/admin/sellers/${sellerId}/toggle`, {}, { headers });
            toast.success(res.data.is_active ? "Machann aktive!" : "Machann dezaktive!");
            fetchSellers();
        } catch (error) {
            toast.error("Erè nan chanje estati machann nan.");
        }
    };

    const handleEditSeller = (seller) => {
        setEditingSeller(seller);
        setEditData({ email: seller.email || "", password: "", phone: seller.phone || "" });
        setEditModalOpen(true);
    };

    const saveSellerChanges = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            await axios.put(`${API}/admin/sellers/${editingSeller.id}`, editData, { headers });
            toast.success("Enfòmasyon machann nan modifye ak siksè!");
            setEditModalOpen(false);
            fetchSellers();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Eksepte voye mesaj la echwe.");
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (ts) => {
        if (!ts) return "N/A";
        const date = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
        return date.toLocaleDateString('fr-HT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <EnterpriseLayout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-[#232F3E]">Jesyon Machann</h1>
                        <p className="text-gray-500 mt-1">Kontwole tout moun k ap vann sou Marketplace la.</p>
                    </div>
                    <div className="bg-white px-6 py-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Shield className="w-5 h-5 text-green-600" />
                        </div>
                        <span className="font-bold text-gray-900 text-xl">{sellers.length} Machann aktive</span>
                    </div>
                </div>

                <Card className="border-0 shadow-lg overflow-hidden bg-white">
                    <CardHeader className="bg-gray-50/50 p-6 border-b border-gray-100 flex flex-row items-center justify-between">
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                            <Store className="w-5 h-5 text-[#FF9900]" /> Lis Machann yo
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-24 text-center text-gray-400 flex flex-col items-center gap-4">
                                <Loader2 className="w-8 h-8 animate-spin text-[#FF9900]" />
                                <p className="font-medium">Ap chaje lis machann yo...</p>
                            </div>
                        ) : sellers.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50/50 text-gray-500 text-xs uppercase font-bold border-b border-gray-100">
                                            <th className="px-6 py-4">Non Machann</th>
                                            <th className="px-6 py-4">Store</th>
                                            <th className="px-6 py-4">Kontak</th>
                                            <th className="px-6 py-4 text-center">Estati</th>
                                            <th className="px-6 py-4 text-right">Aksyon</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {sellers.map((seller) => (
                                            <tr key={seller.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-6 py-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold shadow-sm ${seller.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-400'}`}>
                                                            {seller.fullname.charAt(0)}
                                                        </div>
                                                        <p className="font-bold text-gray-900">{seller.fullname}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6">
                                                    <p className="text-sm font-medium text-gray-600">{seller.store_name || "Boutik San Non"}</p>
                                                </td>
                                                <td className="px-6 py-6">
                                                    <div className="space-y-0.5">
                                                        <p className="text-xs text-gray-500 flex items-center gap-1.5"><Mail className="w-3 h-3" /> {seller.email}</p>
                                                        {seller.phone && <p className="text-xs text-gray-500 flex items-center gap-1.5"><Phone className="w-3 h-3" /> {seller.phone}</p>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6 text-center">
                                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-tight ${seller.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                                        {seller.is_active ? "Aktive" : "Sispann"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-6 text-right flex items-center justify-end gap-2">
                                                    <Button
                                                        onClick={() => handleEditSeller(seller)}
                                                        variant="outline"
                                                        size="sm"
                                                        className="font-bold text-[#FF9900] hover:bg-orange-50 border-orange-100"
                                                    >
                                                        <Edit2 className="w-4 h-4 mr-1.5" /> Modifye
                                                    </Button>
                                                    <Button
                                                        onClick={() => toggleSellerStatus(seller.id)}
                                                        variant="outline"
                                                        size="sm"
                                                        className={`font-bold transition-all ${seller.is_active ? 'text-red-500 hover:bg-red-50 border-red-100' : 'text-green-600 hover:bg-green-50 border-green-100'}`}
                                                    >
                                                        {seller.is_active ? (
                                                            <><UserX className="w-4 h-4 mr-1.5" /> Dezaktive</>
                                                        ) : (
                                                            <><UserCheck className="w-4 h-4 mr-1.5" /> Aktive</>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-24 text-center text-gray-400">
                                <Users className="w-12 h-12 mx-auto mb-4 opacity-10" />
                                <p className="font-medium">Pa gen machann enskri pou kounye a.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
                    <DialogContent className="sm:max-w-md rounded-[30px] p-8 border-none shadow-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-black uppercase tracking-tighter italic">Modifye Machann</DialogTitle>
                            <DialogDescription className="text-gray-500 font-medium">
                                Mete konnye a bon imèl osinon chanje modpas pou {editingSeller?.fullname}.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={saveSellerChanges} className="space-y-4 pt-4 border-t border-gray-100">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Imèl / Username</label>
                                <Input 
                                    value={editData.email}
                                    onChange={e => setEditData({...editData, email: e.target.value})}
                                    className="bg-gray-50 border-none font-bold text-black focus:ring-2 focus:ring-[#FF9900]"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Nimewo Telefòn</label>
                                <Input 
                                    value={editData.phone}
                                    onChange={e => setEditData({...editData, phone: e.target.value})}
                                    className="bg-gray-50 border-none font-bold text-black focus:ring-2 focus:ring-[#FF9900]"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Nouvo Modpas (Opsyonèl)</label>
                                <Input 
                                    type="text"
                                    placeholder="Kite vid si'w pa vle chanje modpas la"
                                    value={editData.password}
                                    onChange={e => setEditData({...editData, password: e.target.value})}
                                    className="bg-gray-50 border-none font-bold text-black focus:ring-2 focus:ring-[#FF9900]"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                disabled={saving} 
                                className="w-full bg-[#FF9900] hover:bg-black text-white h-12 rounded-xl mt-4 font-black"
                            >
                                {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "SOVGADE CHANJMAN YO"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </EnterpriseLayout>
    );
};
